import Popover, { PopoverProps } from './Popover'
import cn from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'

export interface TooltipProps extends Omit<PopoverProps, 'show'> {
  className?: string
  triggerMode?: 'hover' | 'click'
  disabled?: boolean
  /**
   * @description The duration how long the tooltip will be visible after the user has mouseover the trigger element. Only work in `hover` trigger mode.
   */
  exitDelay?: number
  onVisibilityChange?: (visibility: boolean) => void
  dismissOnInteraction?: boolean
  dismissOnMouseLeave?: boolean
  wrapperClassName?: string
  contentClassName?: string
}

const Tooltip = ({
  children,
  content,
  triggerMode = 'hover',
  exitDelay = 0,
  disabled,
  onVisibilityChange,
  dismissOnInteraction = false,
  dismissOnMouseLeave = false,
  wrapperClassName,
  contentClassName,
  ...props
}: TooltipProps) => {
  const [show, setShow] = useState(false)
  const timer = useRef<NodeJS.Timeout | null>(null)
  const onHoverTrigger = useCallback(() => {
    if (triggerMode === 'hover') {
      setShow(!disabled)
      if (timer.current) {
        clearTimeout(timer.current)
      }
    }
  }, [disabled, triggerMode])
  const onHoverShutdown = useCallback(() => {
    if (triggerMode === 'hover' || dismissOnMouseLeave) {
      timer.current = setTimeout(() => {
        setShow(false)
      }, exitDelay)
    }
  }, [exitDelay, triggerMode, dismissOnMouseLeave])

  useEffect(() => {
    onVisibilityChange?.(show)
  }, [onVisibilityChange, show])

  useEffect(() => {
    const listener = () => {
      setShow(false)
    }
    document.addEventListener('click', listener)
    return () => document.removeEventListener('click', listener)
  }, [])

  return (
    <Popover
      content={
        <div
          className={cn(
            typeof content === 'string' && 'p-4 rounded-md bg-white shadow-md',
            props?.dark && 'bg-dark text-white p-4 rounded-md',
            contentClassName
          )}
          onMouseEnter={onHoverTrigger}
          onMouseLeave={onHoverShutdown}
          onClick={(e) => {
            e.stopPropagation()
            if (dismissOnInteraction) setShow(false)
          }}
        >
          {content}
        </div>
      }
      show={show}
      {...props}
    >
      <div
        className={cn(
          wrapperClassName ? wrapperClassName : 'inline-block leading-none'
        )}
        tabIndex={10000}
        onMouseEnter={onHoverTrigger}
        onMouseLeave={onHoverShutdown}
        onClick={(e) => {
          e.stopPropagation()
          return triggerMode === 'click' ? setShow(!disabled) : null
        }}
        onFocusCapture={() => {
          return triggerMode === 'click' ? setShow(!disabled) : null
        }}
      >
        {children}
      </div>
    </Popover>
  )
}

export default Tooltip
